import { formatEur, type ExpensesScheduleDto, type SchedulePeriod } from "@gladis/shared";
import { Button } from "@gladis/ui";
import { formatDate, formatMonth, useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

import { dayDifference } from "../../services/payment-schedule.js";

/** How many payments show before "Show all" — enough to answer "what is next?" without pushing the block down. */
const VISIBLE_BY_DEFAULT = 3;

/**
 * "Coming up" — every dated payment from today to the end of the period, in date order (FR-025 to FR-027).
 *
 * ITS TOTAL IS THE HEADLINE'S FIGURE, read from the same reply rather than summed again here, so the two cannot
 * disagree by a rounding. The header's total is ALWAYS the whole remainder, also while the list is collapsed —
 * it describes the period, not the rows on screen.
 *
 * It is also the chart's TEXT EQUIVALENT: a semantic list with an accessible name, so a screen reader gets every
 * payment the line steps down by without hovering anything. The badge's exact date therefore travels as
 * visually-hidden text as well as a hover title, because a title is invisible to a keyboard and to most readers.
 *
 * A payment due today is listed and marked — still to go out until the day is over (clarification Q3).
 */
export function ComingUpList({
  data,
  period,
}: {
  data: ExpensesScheduleDto;
  period: SchedulePeriod;
}): ReactNode {
  const t = useT();
  const [expanded, setExpanded] = useState(false);

  const upcoming = data.steps
    .filter((step) => step.date >= data.asOf)
    .flatMap((step) => step.payments.map((payment) => ({ date: step.date, payment })));
  const visible = expanded ? upcoming : upcoming.slice(0, VISIBLE_BY_DEFAULT);

  // The year view groups by month (FR-026); a week or a month is one group with no heading.
  const groups = new Map<string, typeof visible>();
  for (const item of visible) {
    const key = period === "year" ? item.date.slice(0, 7) : "all";
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }

  return (
    <section aria-labelledby="schedule-coming-up" data-testid="schedule-coming-up">
      <header className="mb-2 flex items-baseline justify-between gap-3">
        <h3 id="schedule-coming-up" className="font-semibold">
          {t("exp.schedule.comingUp.heading")}
        </h3>
        <span className="text-sm tabular-nums text-muted">
          {formatEur(data.totals.remainingInEuros)}
        </span>
      </header>

      {upcoming.length === 0 ? (
        <p className="text-sm text-muted">{t("exp.schedule.comingUp.nothingLeft")}</p>
      ) : (
        <>
          {[...groups.entries()].map(([key, items]) => (
            <div key={key} className="mb-2">
              {key === "all" ? null : (
                <h4 className="mt-2 text-xs font-medium uppercase tracking-wide text-muted">
                  {formatMonth(Number(key.slice(0, 4)), Number(key.slice(5, 7)))}
                </h4>
              )}
              <ol aria-label={t("exp.schedule.comingUp.heading")} className="flex flex-col gap-1.5">
                {items.map(({ date, payment }) => (
                  <li
                    key={`${date}-${payment.rowKey}`}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <DaysLeftBadge date={date} asOf={data.asOf} />
                      <span className="min-w-0 truncate">{payment.description}</span>
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {formatEur(payment.amountInEuros)}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
          {upcoming.length > VISIBLE_BY_DEFAULT ? (
            <Button
              className="w-full"
              aria-expanded={expanded}
              onClick={() => {
                setExpanded((open) => !open);
              }}
            >
              {expanded
                ? t("exp.schedule.comingUp.showFewer")
                : t("exp.schedule.comingUp.showAll", { count: upcoming.length })}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * How long until the payment goes out — "Today", "1 day", "12 days". The exact date is the hover title, and
 * visually-hidden text for everyone who cannot hover.
 */
export function DaysLeftBadge({ date, asOf }: { date: string; asOf: string }): ReactNode {
  const t = useT();
  const days = dayDifference(asOf, date);
  const exact = formatDate(date);

  return (
    <span
      title={exact}
      className={`shrink-0 rounded-full border border-line px-2 py-0.5 text-xs tabular-nums whitespace-nowrap ${
        days === 0 ? "font-medium text-ink" : "text-muted"
      }`}
    >
      {days === 0 ? t("exp.schedule.today") : t("exp.schedule.comingUp.daysLeft", { count: days })}
      <span className="sr-only">, {exact}</span>
    </span>
  );
}
